import React, { FC, memo } from "react";
import { useTranslation } from "react-i18next";
import { useMedia } from "react-use";

import { Anchor } from "@/components/Anchor";
import { Button } from "@/components/Button";
import { Header } from "@/components/Header";
import { Text } from "@/components/Text";
import { TextListWithDot } from "@/components/TextListWithDot";
import { MEDIA_SIZE_SM } from "@/core/media";
import { IConsultantProfile } from "@/data/consultant-profiles";
import { formatLocation } from "@/data/locations";
import { formatFullName } from "@/data/persons";
import { Flex, IFlexProps } from "@/eworkitComponents/components";
import { Avatar } from "@/views/components/Person";
import { router } from "@/views/router";

export interface IConsultantProfileBusinessCardProps extends IFlexProps {
  profile: IConsultantProfile;
  onChangeProfileClick?: () => void;
}

export const ConsultantProfileBusinessCard: FC<IConsultantProfileBusinessCardProps> = memo(
  ({ profile, onChangeProfileClick, children, ...props }) => {
    const { t } = useTranslation("session");
    const isSmallScreen = useMedia(MEDIA_SIZE_SM);

    return (
      <Flex width={12} direction="row" innerMargin="md" align="flex-start" {...props}>
        <Avatar
          avatar={profile.avatar}
          firstName={profile.firstName}
          lastName={profile.lastName}
          size="xl"
          theme="secondary"
          hjSuppress
        />

        <Flex grow={1} direction={isSmallScreen ? "column" : "row"} innerMargin="md">
          <Flex grow={1} direction="column" innerMargin="xs">
            <Header color="black" size="sm" weight="bold" className="-word-break-all">
              {formatFullName(profile)}
            </Header>

            <TextListWithDot>
              <Text>{profile?.position?.name || ""}</Text>
              {profile.location && <Text>{formatLocation(profile.location)}</Text>}
            </TextListWithDot>

            <Anchor
              align="left"
              target="_blank"
              href={router.get("consultant-profiles.detail", { consultantProfileId: profile.id }).path}>
              {t("JOB_REQUESTS.APPLY.CONSULTANT_PROFILE_FORM.SHOW_DETAILS")}
            </Anchor>
          </Flex>

          <Flex align="flex-start">
            {onChangeProfileClick && (
              <Button variant="secondary" size="lg" onClick={onChangeProfileClick}>
                {t("JOB_REQUESTS.APPLY.CONSULTANT_PROFILE_FORM.CHANGE_PROFILE")}
              </Button>
            )}

            {children}
          </Flex>
        </Flex>
      </Flex>
    );
  },
);
